import { Controller } from '@hotwired/stimulus';
import Dropzone from 'dropzone';

export default class extends Controller {
  static targets = ['info', 'counter', 'confirmOverwriteCheckbox'];
  static values = {
    url: String,
    deleteUrl: String,
    elementId: String,
    confirmOverwrite: Boolean,
    confirmOverwriteMessage: String
  };

  connect() {
    this.csrfToken = this.getCsrfToken();
    this.dropzone = this.initializeDropzone();
    this.setupEventListeners();
  }

  getCsrfToken() {
    const csrfToken = document.querySelector('meta[name="csrf-token"]');
    return csrfToken ? csrfToken.getAttribute('content') : '';
  }

  initializeDropzone() {
    const dropzoneElementId = this.hasElementIdValue
      ? this.elementIdValue
      : '#dropzone-target';

    return new Dropzone(dropzoneElementId, {
      url: this.urlValue,
      addRemoveLinks: this.hasDeleteUrlValue,
      headers: {
        'X-CSRF-Token': this.csrfToken
      },
      acceptedFiles:
        'image/jpeg,image/webp,image/png,image/apng,image/avif,image/gif,image/tiff'
    });
  }

  setupEventListeners() {
    this.dropzone.on('addedfile', this.handleFileAdded.bind(this));
    this.dropzone.on('removedfile', this.handleFileRemoved.bind(this));
    this.dropzone.on('success', this.handleUploadSuccess.bind(this));
    this.dropzone.on('error', this.handleUploadError.bind(this));
  }

  handleFileAdded(file) {
    this.updateFileCounter((v) => v + 1);
    if (this.hasInfoTarget) {
      this.infoTarget.style.display = 'flex';
    }
  }

  handleFileRemoved(file) {
    this.updateFileCounter((v) => v - 1);

    // Handle error recovery for remaining files
    this.handleErrorRecovery();

    // Delete file from server if it was successfully uploaded
    if (file.previewElement.dataset.fileId && this.hasDeleteUrlValue) {
      this.deleteFileFromServer(file.previewElement.dataset.fileId);
    }
  }

  handleUploadSuccess(file, response) {
    file.previewElement.dataset.fileId = response.fileId;
  }

  handleUploadError(file, errorMessage, xhr) {
    if (xhr && xhr.response) {
      try {
        const response = JSON.parse(xhr.response);
        if (response.can_overwrite) {
          if (this.shouldConfirmOverwrite()) {
            this.showOverwriteConfirmation(file, response.error);
          } else {
            this.retryUploadWithOverwrite(file);
          }
          return;
        }
        this.showFileError(file, response.error || errorMessage);
      } catch (e) {
        this.showFileError(file, errorMessage);
      }
    } else {
      this.showFileError(file, errorMessage);
    }
  }

  handleErrorRecovery() {
    const erroredFiles = this.dropzone.files.filter((file) => file.status === 'error');
    if (erroredFiles.length > 0 && this.counterTarget.value > 0) {
      erroredFiles.forEach((file) => {
        this.clearFileError(file);
        file.status = 'queued';
      });
      this.dropzone.processQueue();
    }
  }

  clearFileError(file) {
    const errorMessageElement = file.previewElement.querySelector('.dz-error-message');
    const errorMarkElement = file.previewElement.querySelector('.dz-error-mark');

    if (errorMessageElement) {
      errorMessageElement.remove();
    }
    if (errorMarkElement) {
      errorMarkElement.remove();
    }

    file.previewElement.classList.remove('dz-error');
  }

  showFileError(file, message) {
    const errorElement = file.previewElement.querySelector('.dz-error-message');
    if (errorElement) {
      errorElement.textContent = message;
    }
    file.previewElement.classList.add('dz-error');
  }

  showOverwriteConfirmation(file, errorMessage) {
    const confirmationMessage =
      this.hasConfirmOverwriteMessageValue && this.confirmOverwriteMessageValue
        ? this.confirmOverwriteMessageValue
        : 'Möchtest du das Foto wirklich überschreiben?';
    const confirmed = window.confirm(`${errorMessage}\n\n${confirmationMessage}`);

    if (confirmed) {
      this.retryUploadWithOverwrite(file);
    }
  }

  shouldConfirmOverwrite() {
    if (this.hasConfirmOverwriteCheckboxTarget) {
      return this.confirmOverwriteCheckboxTarget.checked;
    }

    return this.hasConfirmOverwriteValue ? this.confirmOverwriteValue : true;
  }

  async retryUploadWithOverwrite(file) {
    try {
      file.status = 'uploading';
      file.previewElement.classList.add('dz-uploading');
      file.previewElement.classList.remove('dz-error');

      const formData = new FormData();
      formData.append('file', file);
      formData.append('overwrite', 'true');

      const response = await fetch(this.urlValue, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': this.csrfToken
        },
        body: formData
      });

      const data = await response.json();

      if (response.ok && data.ok) {
        file.status = 'success';
        file.previewElement.classList.add('dz-success');
        file.previewElement.dataset.fileId = data.fileId;
        file.previewElement.classList.remove('dz-error');
      } else {
        this.showFileError(file, data.error || 'Upload failed');
      }
    } catch (error) {
      this.showFileError(file, 'Upload failed');
    } finally {
      file.previewElement.classList.remove('dz-uploading');
    }
  }

  async deleteFileFromServer(fileId) {
    try {
      const url = new URL(this.deleteUrlValue);
      await fetch(url, {
        method: 'DELETE',
        mode: 'cors',
        cache: 'no-cache',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken
        },
        body: JSON.stringify({ file_id: fileId })
      });
    } catch (error) {
      console.error('Failed to delete file from server:', error);
    }
  }

  updateFileCounter(operation) {
    if (this.hasCounterTarget) {
      this.counterTarget.value = operation(Number.parseInt(this.counterTarget.value));
      const event = new Event('change');
      this.counterTarget.dispatchEvent(event);
    }
  }

  disconnect() {
    if (this.dropzone) {
      this.dropzone.destroy();
    }
  }
}
